import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface AcknowledgedPolicy {
  policy_id: string;
  policy_name: string;
  acknowledged_at: string;
}

export function useMyAcknowledgedPolicies() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['my-acknowledged-policies', profile?.id],
    queryFn: async (): Promise<AcknowledgedPolicy[]> => {
      if (!profile?.id) return [];

      const { data: acks, error } = await supabase
        .from('policy_acknowledgements')
        .select('acknowledged_at, policy_version_id')
        .eq('acknowledged_by', profile.id)
        .order('acknowledged_at', { ascending: false });

      if (error) throw error;
      if (!acks?.length) return [];

      // Get version -> policy mapping
      const versionIds = acks.map(a => a.policy_version_id);
      const { data: versions, error: vErr } = await supabase
        .from('policy_versions')
        .select('id, policy_id')
        .in('id', versionIds);

      if (vErr) throw vErr;
      if (!versions?.length) return [];

      const policyIds = [...new Set(versions.map(v => v.policy_id))];
      const { data: policies, error: pErr } = await supabase
        .from('policies')
        .select('id, name')
        .in('id', policyIds);

      if (pErr) throw pErr;

      const policyMap = new Map(policies?.map(p => [p.id, p.name]) ?? []);
      const versionMap = new Map(versions.map(v => [v.id, v.policy_id]));

      // Deduplicate: show only the latest ack per policy
      const seen = new Set<string>();
      const result: AcknowledgedPolicy[] = [];

      for (const ack of acks) {
        const policyId = versionMap.get(ack.policy_version_id);
        if (!policyId || seen.has(policyId)) continue;
        seen.add(policyId);
        result.push({
          policy_id: policyId,
          policy_name: policyMap.get(policyId) ?? 'Unknown Policy',
          acknowledged_at: ack.acknowledged_at,
        });
      }

      return result;
    },
    enabled: !!profile?.id,
  });
}
